import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { identity, isNumber, pickBy } from 'lodash';
import { Observable } from 'rxjs';
import { v4 } from 'uuid';
import { GlobalBannerService } from '../shared/components/global-banner/global-banner.service';
import { IEntityDataDispatcher } from './entity-data-dispatcher.interface';
import {
  addToLimitedSelectedList,
  addToSelectedList,
  clearFilters,
  clearList,
  clearMasterError,
  createNewEditingEntity,
  deleteEntityNetwork,
  deleteEntityStore,
  fetchFilters,
  fetchList,
  fetchNextPage,
  getEntityById,
  refreshEditingEntity,
  removeFromSelectedList,
  resetEditingEntity,
  resetSelectedList,
  saveEditingEntity,
  saveEntityStore,
  saveNewEntity,
  searchList,
  setEditingEntity,
  setEditingEntityContent,
  setEditingEntityField,
  setEditingEntityProperties,
  updateEntity,
  deleteSelectedEntities,
} from './entity-data.actions';
import {
  IEntity,
  ISearchParams,
  SortByEnum,
  SortOrderEnum,
} from './entity-data.data';
import { IEntityState } from './entity-data.reducer';
import { EntityDataStore } from './entity-data.store';
import { EntityDataBuilder } from './entity-data.builder';
import { EntityTypesEnum } from '../../app.enum';
import { Map } from 'immutable';

// TODO [comments]: add comments
// should only be used as a superclass; this class is not injectable
export class EntityDataDispatcher<T extends IEntityState, U extends IEntity>
  implements IEntityDataDispatcher {
  protected entityType: EntityTypesEnum;
  protected yearFilterKey: string;

  constructor(
    protected store: Store<any>,
    protected entityDataStore: EntityDataStore<T, U>,
    protected entityDataBuilder: EntityDataBuilder<U>,
    protected globalBannerService: GlobalBannerService,
    protected translateService: TranslateService
  ) {
    this.entityType = entityDataBuilder.entityType;
    this.yearFilterKey = entityDataBuilder.yearFilterKey;
  }

  fetchList(listFilter: string) {
    this.store.dispatch(fetchList(this.entityType, {}, listFilter));
  }

  sortList(sortBy: SortByEnum, sortOrder: SortOrderEnum, listFilter: string) {
    const sortParams: ISearchParams = {
      page: 1,
      sort_by: sortBy,
      sort_order: sortOrder,
    };

    this.store.dispatch(fetchList(this.entityType, sortParams, listFilter));
  }

  filterList(year: number, listFilter: string) {
    const filterParams: ISearchParams = {
      page: 1,
      [this.yearFilterKey || 'year']: year,
    };

    this.store.dispatch(fetchList(this.entityType, filterParams, listFilter));
  }

  fetchNextPage(listFilter: string) {
    this.store.dispatch(fetchNextPage(this.entityType, listFilter));
  }

  clearList(listFilter: string) {
    this.store.dispatch(clearList(this.entityType, listFilter));
  }

  searchList(
    query: string,
    sortBy: SortByEnum,
    sortOrder: SortOrderEnum,
    highlights: boolean,
    globalSearch?: boolean,
    persistParams?: boolean,
    extraParams?: any
  ) {
    const searchParams: ISearchParams = pickBy(
      {
        query,
        sort_by: sortBy,
        sort_order: sortOrder,
        highlights,
        ...extraParams,
      },
      identity
    );

    this.store.dispatch(
      searchList(this.entityType, searchParams, globalSearch, persistParams)
    );
  }

  fetchFilters(listFilter: string) {
    this.store.dispatch(fetchFilters(this.entityType, 0, listFilter));
  }

  clearFilters(listFilter: string) {
    this.store.dispatch(clearFilters(this.entityType, listFilter));
  }

  getEntityById(entityId: string, useLocal: boolean = false): Observable<U> {
    const dispatchId = v4();

    this.store.dispatch(
      getEntityById(dispatchId, this.entityType, entityId, useLocal)
    );

    return this.entityDataStore.getDispatchedResponse<U>(dispatchId);
  }

  saveNewEntity(entity: U): Observable<U> {
    const dispatchId = v4();

    this.store.dispatch(saveNewEntity(dispatchId, this.entityType, entity));

    return this.entityDataStore.getDispatchedResponse<U>(dispatchId);
  }

  updateEntity(
    entityId: string,
    contentProps: Map<string, any>
  ): Observable<U> {
    const dispatchId = v4();

    this.store.dispatch(
      updateEntity(dispatchId, this.entityType, entityId, contentProps)
    );

    return this.entityDataStore.getDispatchedResponse<U>(dispatchId);
  }

  saveEditingEntity(): Observable<U | any> {
    const dispatchId = v4();

    this.store.dispatch(saveEditingEntity(dispatchId, this.entityType));

    return this.entityDataStore.getDispatchedResponse<U>(dispatchId);
  }

  saveEntityToStore(
    entity: U,
    useLocalId: boolean = false,
    extraListFilter?: string
  ) {
    this.store.dispatch(saveEntityStore(this.entityType, entity, useLocalId));

    if (extraListFilter) {
      this.store.dispatch(
        saveEntityStore(this.entityType, entity, useLocalId, extraListFilter)
      );
    }
  }

  deleteEntityFromStore(entity: U) {
    this.store.dispatch(deleteEntityStore(this.entityType, entity, !entity.id));
  }

  deleteEntityFromNetwork(entity: U): Observable<U> {
    const dispatchId = v4();

    this.store.dispatch(
      deleteEntityNetwork(dispatchId, this.entityType, entity)
    );

    return this.entityDataStore.getDispatchedResponse<U>(dispatchId);
  }

  createNewEditingEntity(goBackUrl?: string) {
    this.store.dispatch(createNewEditingEntity(this.entityType, goBackUrl));
  }

  setEditingEntity(entity: U) {
    this.store.dispatch(setEditingEntity(this.entityType, entity));
  }

  refreshEditingEntity(saveEntityToStore: boolean = false) {
    this.store.dispatch(
      refreshEditingEntity(this.entityType, saveEntityToStore)
    );
  }

  setEditingEntityContent(
    properties: Map<string, any>,
    saveEditing: boolean = false
  ): Observable<U> {
    const dispatchId = v4();

    this.store.dispatch(
      setEditingEntityContent(
        dispatchId,
        this.entityType,
        properties,
        saveEditing
      )
    );

    return this.entityDataStore.getDispatchedResponse<U>(dispatchId);
  }

  setEditingEntityProperties(properties: Map<string, any>) {
    this.store.dispatch(
      setEditingEntityProperties(this.entityType, properties)
    );
  }

  setEditingEntityField(key: string | string[], value: any) {
    this.store.dispatch(setEditingEntityField(this.entityType, key, value));
  }

  resetEditingEntity() {
    this.store.dispatch(resetEditingEntity(this.entityType));
  }

  addToSelectedList(entity: U, limit?: number) {
    if (isNumber(limit)) {
      this.store.dispatch(
        addToLimitedSelectedList(this.entityType, entity, limit)
      );
    } else {
      this.store.dispatch(addToSelectedList(this.entityType, entity));
    }
  }

  removeFromSelectedList(entity: U) {
    this.store.dispatch(removeFromSelectedList(this.entityType, entity));
  }

  resetSelectedList() {
    this.store.dispatch(resetSelectedList(this.entityType));
  }

  clearMasterError() {
    this.store.dispatch(clearMasterError());
  }

  deleteSelectedEntities(listFilter?: string): Observable<any> {
    const dispatchId = v4();

    this.store.dispatch(
      deleteSelectedEntities(dispatchId, this.entityType, listFilter)
    );

    return this.entityDataStore.getDispatchedResponse<any>(dispatchId);
  }
}
